/**
 * Watch progress storage (Sprint 3 B6) — local-only, per learner per lesson.
 *
 * Sprint 3: lưu trên `localStorage` của browser → đơn giản, không gọi backend.
 * Sprint 6 (xAPI LRS): sync lên server qua `posted_at_seconds` event để cross-device.
 *
 * Key format: `sep2026-progress-<lessonSlug>` → JSON {currentTime, duration, savedAt}
 *
 * Em throttle save mỗi 5s để tránh spam localStorage write (mỗi write block
 * main thread ~1-2ms khi data > 1MB).
 *
 * Resume guard: nếu user đã xem hết (currentTime > duration - 10s) hoặc
 * mới bắt đầu (< 5s) thì KHÔNG resume — start từ đầu cho UX tốt.
 */

const KEY_PREFIX = "sep2026-progress-";
const SAVE_THROTTLE_MS = 5_000;
const RESUME_MIN_SECONDS = 5;
const RESUME_END_THRESHOLD_SECONDS = 10;

export interface WatchProgress {
  currentTime: number;
  duration: number;
  savedAt: number; // epoch ms
}

function storageKey(lessonSlug: string): string {
  return `${KEY_PREFIX}${lessonSlug}`;
}

/** Đọc progress của 1 lesson. Trả null nếu chưa có hoặc localStorage lỗi. */
export function readProgress(lessonSlug: string): WatchProgress | null {
  try {
    const raw = window.localStorage.getItem(storageKey(lessonSlug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WatchProgress;
    if (typeof parsed.currentTime !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Lưu progress. Throttle nội bộ — gọi nhiều lần nhanh vẫn chỉ ghi sau 5s. */
const lastWriteAt = new Map<string, number>();

export function saveProgress(
  lessonSlug: string,
  currentTime: number,
  duration: number,
): void {
  if (!Number.isFinite(currentTime) || currentTime <= 0) return;
  const now = Date.now();
  const last = lastWriteAt.get(lessonSlug) ?? 0;
  if (now - last < SAVE_THROTTLE_MS) return;
  try {
    window.localStorage.setItem(
      storageKey(lessonSlug),
      JSON.stringify({ currentTime, duration, savedAt: now } satisfies WatchProgress),
    );
    lastWriteAt.set(lessonSlug, now);
  } catch {
    // Quota exceeded / disabled — silently fail, không break playback
  }
}

/**
 * Quyết định có nên resume hay không + trả về thời điểm cần seek tới.
 *
 * Trả null nếu:
 *   - Chưa có progress
 *   - Đã xem gần hết (< 10s tới end)
 *   - Mới bắt đầu (< 5s)
 *
 * Có currentTime → seek tới đó khi player ready.
 */
export function getResumeTime(lessonSlug: string): number | null {
  const progress = readProgress(lessonSlug);
  if (!progress) return null;
  if (progress.currentTime < RESUME_MIN_SECONDS) return null;
  if (
    progress.duration > 0 &&
    progress.currentTime > progress.duration - RESUME_END_THRESHOLD_SECONDS
  ) {
    return null;
  }
  return progress.currentTime;
}

/** Xoá progress (gọi khi xem hết bài hoặc reset). */
export function clearProgress(lessonSlug: string): void {
  try {
    window.localStorage.removeItem(storageKey(lessonSlug));
    lastWriteAt.delete(lessonSlug);
  } catch {
    // ignore
  }
}

/** Test helper — reset throttle state giữa các test. */
export function _resetThrottleForTest(): void {
  lastWriteAt.clear();
}
